import React, { useState, useCallback, useRef, useEffect } from 'react';
import LifecycleBadge from './LifecycleBadge';
import SourceBadge from './SourceBadge';
import PlaybookStructureTab from './PlaybookStructureTab';
import PlaybookGuardsTab from './PlaybookGuardsTab';
import PlaybookActionsTab from './PlaybookActionsTab';
import PlaybookUsageTab from './PlaybookUsageTab';
import AnalyticsDashboard from './AnalyticsDashboard';
import VersionHistoryTab from './VersionHistoryTab';
import PlaybookSimulationView from './PlaybookSimulationView';
import PublishValidationModal from './PublishValidationModal';
import WebhookMonitor from './WebhookMonitor';
import PlaybookEditorCanvas from './editor/PlaybookEditorCanvas';
import { usePlaybookDetail } from '../hooks/usePlaybookDetail';
import { PLAYBOOK_KEYFRAMES } from '../types/playbook.types';
import type { PlaybookEntity } from '../types/playbook.types';

interface PlaybookDetailViewProps {
  playbookId: string;
  onBack: () => void;
}

type DetailTab = 'editor' | 'structure' | 'guards' | 'actions' | 'usage' | 'analytics' | 'versions' | 'simulation' | 'webhooks';

const TABS: Array<{ id: DetailTab; label: string; editorOnly?: boolean }> = [
  { id: 'editor', label: 'Visual Editor', editorOnly: true },
  { id: 'structure', label: 'Structure' },
  { id: 'guards', label: 'Guards & Validators' },
  { id: 'actions', label: 'Actions' },
  { id: 'usage', label: 'Usage' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'versions', label: 'Versions' },
  { id: 'webhooks', label: 'Webhooks' },
];

function useSlidingIndicator(activeTab: DetailTab) {
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const measure = useCallback(() => {
    const el = tabRefs.current[activeTab];
    const container = containerRef.current;
    if (el && container) {
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      setIndicator({ left: elRect.left - containerRect.left, width: elRect.width });
    }
  }, [activeTab]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const setRef = useCallback(
    (id: string) => (el: HTMLButtonElement | null) => { tabRefs.current[id] = el; },
    [],
  );

  return { indicator, setRef, containerRef };
}

const PlaybookDetailView: React.FC<PlaybookDetailViewProps> = ({ playbookId, onBack }) => {
  const { playbook, loading, error, refresh } = usePlaybookDetail(playbookId);
  const canEdit = !playbook?.is_system && playbook?.lifecycle === 'draft';
  const [activeTab, setActiveTab] = useState<DetailTab>('structure');
  const [contentVisible, setContentVisible] = useState(true);
  const [renderKey, setRenderKey] = useState(0);
  const [showPublish, setShowPublish] = useState(false);
  const [showSimulation, setShowSimulation] = useState(false);
  const [didAutoSwitch, setDidAutoSwitch] = useState(false);

  // Auto-switch to editor tab for editable playbooks on first load
  useEffect(() => {
    if (canEdit && !didAutoSwitch && playbook) {
      setActiveTab('editor');
      setDidAutoSwitch(true);
    }
  }, [canEdit, didAutoSwitch, playbook]);
  const { indicator, setRef, containerRef } = useSlidingIndicator(activeTab);

  const switchTab = useCallback((tab: DetailTab) => {
    setContentVisible(false);
    setTimeout(() => {
      setActiveTab(tab);
      setRenderKey((k) => k + 1);
      requestAnimationFrame(() => setContentVisible(true));
    }, 180);
  }, []);

  const handlePublished = useCallback((_pb: PlaybookEntity) => {
    setShowPublish(false);
    refresh();
  }, [refresh]);

  if (loading) {
    return (
      <div className="flex items-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-lex-accent border-t-transparent" />
        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading playbook...</span>
      </div>
    );
  }

  if (error || !playbook) {
    return (
      <div>
        <button type="button" onClick={onBack} className="text-sm text-lex-accent hover:text-lex-accent-hover mb-2">
          ← Back to Library
        </button>
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
          {error || 'Playbook not found'}
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{PLAYBOOK_KEYFRAMES}</style>
      <div className="space-y-4" style={{ animation: 'fadeSlideIn 300ms ease-out' }}>
        {/* Back + Header */}
        <button type="button" onClick={onBack} className="text-sm text-lex-accent hover:text-lex-accent-hover">
          ← Back to Library
        </button>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{playbook.name}</h2>
            <span className="text-xs text-gray-400">v{playbook.version}</span>
            <LifecycleBadge lifecycle={playbook.lifecycle} />
            <SourceBadge source={playbook.source} />
            {playbook.is_system && (
              <span className="inline-flex items-center rounded-full bg-lex-accent-soft px-2 py-0.5 text-[10px] font-medium text-lex-accent">
                System (Read-only)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowSimulation(true)}
              className="rounded-md border border-lex-accent px-3 py-1.5 text-xs font-medium text-lex-accent hover:bg-lex-state-hover dark:border-lex-accent"
            >
              Test Playbook
            </button>
            {!playbook.is_system && playbook.lifecycle === 'draft' && (
              <button
                type="button"
                onClick={() => setShowPublish(true)}
                className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
              >
                Publish
              </button>
            )}
          </div>
        </div>

        {playbook.description && (
          <p className="text-sm text-gray-500 dark:text-gray-400">{playbook.description}</p>
        )}

        {/* Sub-tab navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div ref={containerRef} className="relative flex overflow-x-auto">
            <div
              className="absolute bottom-0 h-0.5 bg-lex-accent transition-all duration-300"
              style={{ left: indicator.left, width: indicator.width, transitionTimingFunction: 'cubic-bezier(0.4,0,0.2,1)' }}
            />
            {TABS.filter((tab) => !tab.editorOnly || canEdit).map((tab) => (
              <button
                key={tab.id}
                ref={setRef(tab.id)}
                onClick={() => switchTab(tab.id)}
                className={`px-4 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'text-lex-accent'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div
          key={renderKey}
          style={{
            opacity: contentVisible ? 1 : 0,
            transform: contentVisible ? 'translateY(0)' : 'translateY(6px)',
            transition: 'opacity 180ms ease-out, transform 180ms ease-out',
          }}
        >
          {activeTab === 'editor' && canEdit && (
            <PlaybookEditorCanvas
              playbookId={playbook.id}
              slug={playbook.slug}
              canPublish={!playbook.is_system && playbook.lifecycle === 'draft'}
              onPublish={() => setShowPublish(true)}
              onSimulate={() => setShowSimulation(true)}
            />
          )}
          {activeTab === 'structure' && <PlaybookStructureTab playbook={playbook} />}
          {activeTab === 'guards' && <PlaybookGuardsTab playbook={playbook} />}
          {activeTab === 'actions' && <PlaybookActionsTab playbook={playbook} />}
          {activeTab === 'usage' && <PlaybookUsageTab playbook={playbook} />}
          {activeTab === 'analytics' && <AnalyticsDashboard playbookId={playbook.id} slug={playbook.slug} />}
          {activeTab === 'versions' && <VersionHistoryTab playbook={playbook} />}
          {activeTab === 'webhooks' && <WebhookMonitor playbookId={playbook.id} />}
        </div>
      </div>

      {/* Simulation overlay */}
      {showSimulation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowSimulation(false)} />
          <div className="relative w-full max-w-2xl rounded-lg bg-white shadow-xl dark:bg-gray-800 max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Test Playbook</h3>
              <button type="button" onClick={() => setShowSimulation(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                ✕
              </button>
            </div>
            <PlaybookSimulationView playbookId={playbook.id} />
          </div>
        </div>
      )}

      {/* Publish modal */}
      {showPublish && (
        <PublishValidationModal
          isOpen={showPublish}
          playbook={playbook}
          onClose={() => setShowPublish(false)}
          onPublished={handlePublished}
        />
      )}
    </>
  );
};

export default PlaybookDetailView;
